export const DAY_RATE_OPTIONS = [
  "Billable Rate",
  "Principal Rate",
  "Project Manager Rate",
  "Field Agent Rate",
];

const rateConfig = {
  "Principal Rate": {
    flag: ["IsPrincipalRate", "isPrincipalRate"],
    value: ["SelectedPrincipalRate", "selectedPrincipalRate", "PrincipalRate", "principalRate"],
    baseValue: ["PrincipalRate", "principalRate"],
    selected: "selectedPrincipalRate",
  },
  "Project Manager Rate": {
    flag: ["IsProjectManagerRate", "isProjectManagerRate"],
    value: ["SelectedProjectManagerRate", "selectedProjectManagerRate", "ProjectManagerRate", "projectManagerRate"],
    baseValue: ["ProjectManagerRate", "projectManagerRate"],
    selected: "selectedProjectManagerRate",
  },
  "Field Agent Rate": {
    flag: ["IsFieldAgentRate", "isFieldAgentRate"],
    value: ["SelectedFieldAgentRate", "selectedFieldAgentRate", "FieldAgentRate", "fieldAgentRate"],
    baseValue: ["FieldAgentRate", "fieldAgentRate"],
    selected: "selectedFieldAgentRate",
  },
};

export const isEnabledRateFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE";

const firstValue = (source, keys) =>
  keys
    .map((key) => source?.[key])
    .find(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== ""
    );



export const normalizeProjectRateData = (project = {}) => ({
  ...project,
  ProjectID: project.ProjectID ?? project.projectId,
  principalRate: project.PrincipalRate ?? project.principalRate,
  PrincipalRate: project.PrincipalRate ?? project.principalRate,
  projectManagerRate: project.ProjectManagerRate ?? project.projectManagerRate,
  ProjectManagerRate: project.ProjectManagerRate ?? project.projectManagerRate,
  fieldAgentRate: project.FieldAgentRate ?? project.fieldAgentRate,
  FieldAgentRate: project.FieldAgentRate ?? project.fieldAgentRate,
});

export const getLineDayRateOptions = (
  broker = {},
  project = {},
  persistedSelection
) => {
  const options = DAY_RATE_OPTIONS.filter((option) => {
    if (option === "Billable Rate") {
      return true;
    }

    const config = rateConfig[option];

    const brokerFlag = firstValue(broker, config.flag);
    // Optional line-item rates are enabled by the selected broker only.
    // Project rate amounts may supply the value, but must not enable an option.
    return isEnabledRateFlag(brokerFlag);
  });

  // Preserve a saved optional selection even when its broker is no longer
  // assigned to the project. This keeps the existing line editable.
  const savedSelection = normalizeSelectedDayRate(
    persistedSelection ?? project?.selectedDayRate ?? project?.SelectedDayRate
  );
  return options.includes(savedSelection)
    ? options
    : [savedSelection, ...options];
};

export const getPersistedSelectedDayRate = (source = {}) =>
  source.selectedDayRate ?? source.SelectedDayRate ?? source.InvSelectedDayRate;

export const formatRateValue = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
};

export const normalizeSelectedDayRate = (selection) =>
  selection === "Default" || !DAY_RATE_OPTIONS.includes(selection)
    ? "Billable Rate"
    : selection;

export const getDayRateForSelection = (
  selection,
  source = {},
  fallback = 0,
  preferSelectedRate = true
) => {
  const normalized = normalizeSelectedDayRate(selection);
  const billable = ["BillingDayRate", "BillableDayRate", "billingDayRate", "billableDayRate", "MatchedDayRate", "dayRate"];
  const keys = normalized === "Billable Rate"
    ? billable
    : preferSelectedRate
      ? rateConfig[normalized]?.value || []
      : rateConfig[normalized]?.baseValue || [];
  return firstValue(source, keys) ?? fallback;
};

export const getInvoiceSelectionRate = (
  selection,
  invoiceSource = {},
  projectSource = {},
  fallback = 0
) => {
  const normalized = normalizeSelectedDayRate(selection);
  const invoiceKeys = {
    "Billable Rate": ["InvoiceDayRate", "invoiceDayRate"],
    "Principal Rate": ["selectedPrincipalRate", "SelectedPrincipalRate"],
    "Project Manager Rate": [
      "selectedProjectManagerRate",
      "SelectedProjectManagerRate",
    ],
    "Field Agent Rate": ["selectedFieldAgentRate", "SelectedFieldAgentRate"],
  };
  const projectKeys = {
    "Billable Rate": [
      "BillingDayRate",
      "BillableDayRate",
      "billingDayRate",
      "billableDayRate",
      "dayRate",
    ],
    "Principal Rate": ["PrincipalRate", "principalRate"],
    "Project Manager Rate": ["ProjectManagerRate", "projectManagerRate"],
    "Field Agent Rate": ["FieldAgentRate", "fieldAgentRate"],
  };

  return (
    firstValue(invoiceSource, invoiceKeys[normalized] || []) ??
    firstValue(projectSource, projectKeys[normalized] || []) ??
    fallback
  );
};

export const getSelectedRateFields = (selection, dayRate) => {
  const normalized = normalizeSelectedDayRate(selection);
  const config = rateConfig[normalized];
  return config ? { [config.selected]: formatRateValue(dayRate) } : {};
};

// The invoice API keeps the billable amount in dayRate. Optional selected
// rates are additional fields and must not replace dayRate.
export const getLineRatePayload = (
  selection,
  billableRate,
  selectedRate,
  source = {}
) => {
  const normalized = normalizeSelectedDayRate(selection);
  const payload = {
    dayRate: formatRateValue(billableRate),
    selectedDayRate: normalized,
  };

  const existingOptionalRates = {};
  Object.values(rateConfig).forEach((config) => {
    const existing = firstValue(source, [
      config.selected,
      config.selected[0].toUpperCase() + config.selected.slice(1),
    ]);
    if (existing !== undefined) {
      existingOptionalRates[config.selected] = formatRateValue(existing);
    }
  });

  if (rateConfig[normalized]) {
    return {
      ...payload,
      ...existingOptionalRates,
      ...getSelectedRateFields(normalized, selectedRate),
    };
  }

  return { ...payload, ...existingOptionalRates };
};

export const resolveLineDayRate = (
  selection,
  broker,
  project,
  fallback = 0,
  preferSelectedRate = true
) => {
  const options = getLineDayRateOptions(broker, project);
  const normalized = options.includes(selection) ? selection : "Billable Rate";
  const dayRate = getDayRateForSelection(
    normalized,
    { ...project, ...broker },
    fallback,
    preferSelectedRate
  );
  return { selectedDayRate: normalized, dayRate, ...getSelectedRateFields(normalized, dayRate) };
};

// New-line path: billable rate is always sourced from the invoice header.
export const resolveNewLineItemDayRate = (
  selection,
  broker,
  project,
  invoiceDayRate = 0
) => {
  console.log(invoiceDayRate,"====NewLineItem===invoiceDayRate====");
  
  const options = getLineDayRateOptions(broker, project);
  const normalized = options.includes(selection) ? selection : "Billable Rate";
  const dayRate = normalized === "Billable Rate"
    ? invoiceDayRate
    : getDayRateForSelection(normalized, project, 0, false);

  return {
    selectedDayRate: normalized,
    dayRate,
    ...getSelectedRateFields(normalized, dayRate),
  };
};

// Existing-line path: selected invoice-line rate first, project rate second.
export const resolveExistingLineItemDayRate = (
  selection,
  invoiceSource = {},
  projectSource = {},
  fallback = 0
) => {
  console.log(invoiceSource, "=====invoiceSource====");
  console.log(projectSource, "=====projectSource====");
  const normalized = normalizeSelectedDayRate(selection);
  const invoiceKeys = {
    "Billable Rate": ["InvoiceDayRate", "invoiceDayRate"],
    "Principal Rate": ["selectedPrincipalRate", "SelectedPrincipalRate"],
    "Project Manager Rate": [
      "selectedProjectManagerRate",
      "SelectedProjectManagerRate",
    ],
    "Field Agent Rate": ["selectedFieldAgentRate", "SelectedFieldAgentRate"],
  };
  const projectKeys = {
    "Billable Rate": [
      "BillingDayRate",
      "BillableDayRate",
      "billingDayRate",
      "billableDayRate",
      "dayRate",
    ],
    "Principal Rate": ["PrincipalRate", "principalRate"],
    "Project Manager Rate": ["ProjectManagerRate", "projectManagerRate"],
    "Field Agent Rate": ["FieldAgentRate", "fieldAgentRate"],
  };
  const dayRate =
    firstValue(invoiceSource, invoiceKeys[normalized] || []) ??
    firstValue(projectSource, projectKeys[normalized] || []) ??
    fallback;

  return {
    selectedDayRate: normalized,
    dayRate,
    ...getSelectedRateFields(normalized, dayRate),
  };
};

export const buildNewLineItemRatePayload = (
  selection,
  billableRate,
  selectedRate
) => ({
  dayRate: formatRateValue(billableRate),
  selectedDayRate: normalizeSelectedDayRate(selection),
  ...getSelectedRateFields(selection, selectedRate),
});

export const buildExistingLineItemRatePayload = (
  selection,
  billableRate,
  selectedRate,
  source = {}
) => {
  const normalized = normalizeSelectedDayRate(selection);
  const payload = {
    dayRate: formatRateValue(billableRate),
    selectedDayRate: normalized,
  };

  Object.values(rateConfig).forEach((config) => {
    const existing = firstValue(source, [
      config.selected,
      config.selected[0].toUpperCase() + config.selected.slice(1),
    ]);
    if (existing !== undefined) {
      payload[config.selected] = formatRateValue(existing);
    }
  });

  return {
    ...payload,
    ...getSelectedRateFields(normalized, selectedRate),
  };
};
